import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { BaseItemComponent } from '../base-item/base-item.component';

const SEGMENT_LABELS = ['Primer Tramo', 'Segundo Tramo', 'Tercer Tramo', 'Cuarto Tramo', 'Quinto Tramo'];

@Component({
  selector: 'usb-flight-item',
  templateUrl: './flight-item.component.html',
  styleUrls: ['./flight-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlightItemComponent extends BaseItemComponent {
  @Input() item: any;
  @Input() clientsSettingAppBooking: any;

  // El "Ver más/Ver menos" lo maneja el toggle genérico de usb-base-item
  // (isExpanded) — acá solo arma el detalle por segmento que ese toggle
  // muestra u oculta.

  // FlyProductService arma item.itinerary con TODAS las opciones que trajo la
  // búsqueda por tramo, e item.selectedOptions con el optionId realmente
  // reservado por tramo (hoy siempre options[0], no hay UI para elegir otra).
  getSelectedOption(leg: any): any {
    const selectedId = (this.item?.selectedOptions || []).find((optionId: string) =>
      leg?.options?.some((option: any) => option.optionId === optionId)
    );
    return leg?.options?.find((option: any) => option.optionId === selectedId) || leg?.options?.[0];
  }

  getSegments(leg: any): any[] {
    return this.getSelectedOption(leg)?.segments || [];
  }

  getSegmentLabel(index: number): string {
    return SEGMENT_LABELS[index] || `Tramo ${index + 1}`;
  }

  // El proveedor no siempre informa segment.duration (puede venir null) —
  // si falta, se calcula de la diferencia real entre arrival y departure
  // (que ya resuelve la diferencia horaria entre aeropuertos).
  getSegmentDuration(segment: any): number {
    if (segment?.duration) {
      return segment.duration;
    }
    if (!segment?.departure?.dateTime || !segment?.arrival?.dateTime) {
      return 0;
    }
    const departure = new Date(segment.departure.dateTime).getTime();
    const arrival = new Date(segment.arrival.dateTime).getTime();
    return Math.max(0, Math.round((arrival - departure) / 60000));
  }

  // Tiempo de escala ENTRE este segmento y el siguiente del mismo tramo —
  // prefiere segment.layover (minutos, ya lo informa la API) con fallback a
  // la diferencia real entre fechas, igual criterio que usb-base-item.
  getSegmentLayoverMinutes(segments: any[], index: number): number {
    const current = segments[index];
    if (current?.layover != null) {
      return current.layover;
    }
    const next = segments[index + 1];
    if (!current?.arrival?.dateTime || !next?.departure?.dateTime) {
      return 0;
    }
    const arrival = new Date(current.arrival.dateTime).getTime();
    const departure = new Date(next.departure.dateTime).getTime();
    return Math.max(0, Math.round((departure - arrival) / 60000));
  }

  formatDuration(minutes: number): string {
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  }

  // "Incluye" — ver resultReserve.summary (lo informa el proveedor al confirmar
  // la reserva: checkedBags, handBag, changes, refund, seat, meal). Antes de
  // reservar (status 1) este dato todavía no existe, así que los íconos quedan
  // todos atenuados por falta de info, no por estar realmente excluidos.
  private get flightSummary(): any {
    return this.item?.resultReserve?.summary ?? this.item?.summary;
  }

  hasRefund(): boolean {
    return this.flightSummary?.refund === 'INCLUDED';
  }

  hasChanges(): boolean {
    return this.flightSummary?.changes === 'INCLUDED';
  }

  hasSeat(): boolean {
    return this.flightSummary?.seat === 'INCLUDED';
  }

  hasHandBag(): boolean {
    return !!this.flightSummary?.handBag;
  }

  hasCheckedBag(): boolean {
    return (this.flightSummary?.checkedBags || 0) > 0;
  }

  hasMeal(): boolean {
    return !!this.flightSummary?.meal;
  }
}
